// Life on top of the clips: the head looks at things, the tail balances on a spring, the ears
// twitch and relax. Runs after the mixer and the build, every frame, on fresh clip poses.
//
// Rotations are given in the cat's own space (+X its left, +Y up, +Z forward) and applied about
// each bone's pivot.
import { type Bone, MathUtils, type Object3D, Quaternion, Vector3 } from 'three';
import type { Personality } from '../../data/cats';
import { boneName } from './catFactory';
import type { CatMode } from './animator';
import type { PoseLayer } from './podiumIdle';

const X = new Vector3(1, 0, 0);
const Y = new Vector3(0, 1, 0);
const Z = new Vector3(0, 0, 1);
const _q = new Quaternion();
const _pq = new Quaternion();
const _axis = new Vector3();
const _v = new Vector3();

/** Rotates a bone about an axis given in the cat's space. */
function rotate(bone: Bone, catQ: Quaternion, axis: Vector3, angle: number, saved: Map<Bone, Quaternion>): void {
  if (Math.abs(angle) < 1e-5 || !bone.parent) return;
  if (!saved.has(bone)) saved.set(bone, bone.quaternion.clone());
  bone.parent.getWorldQuaternion(_pq).invert();
  _axis.copy(axis).applyQuaternion(catQ).applyQuaternion(_pq).normalize();
  bone.quaternion.premultiply(_q.setFromAxisAngle(_axis, angle));
  bone.updateMatrixWorld(true);
}

export interface SecondaryInput {
  mode: CatMode;
  speed: number;
  /** m/s² along the track. */
  accel: number;
  /** rad/s of heading change (lane drift). */
  yawRate: number;
  /** World point to look at, or null to look ahead. */
  look: Vector3 | null;
  /** The cat's world rotation (cat space → world). */
  catQ: Quaternion;
  /** Podium layers on top of the clip, each 0..1: lying down, stretching, washing the face, tail flicks, looking around. */
  layers?: Partial<Record<PoseLayer, number>>;
}

/** How far the body drops (m, at size 1) when the lie-down layer is full on; the actor lowers the cat by this. */
export const LIE_DROP = 0.015;

export class CatSecondary {
  private head?: Bone;
  private neck?: Bone;
  private ears: [Bone | undefined, Bone | undefined];
  private tail: Bone[];
  private yaw = 0;
  private pitch = 0;
  private tailSway = 0;
  private tailVel = 0;
  private tailLift = 0;
  private earTwitch: [number, number] = [0, 0];
  private nextTwitch: number;
  private rnd: () => number;
  private earRelax = 0;
  private spineBones: (Bone | undefined)[];
  private arms: [Bone | undefined, Bone | undefined];
  private jaw?: Bone;
  private flickAt = 0;
  private flickSide = 1;
  /** Clip rotations of the bones changed this frame, restored before the next mixer update. */
  private saved = new Map<Bone, Quaternion>();

  constructor(private rig: Object3D, bones: Map<string, Bone>, private p: Personality, seed: number) {
    const b = (n: string) => bones.get(boneName(n));
    this.head = b('spine.006');
    this.neck = b('spine.005');
    this.ears = [b('ear.L'), b('ear.R')];
    this.spineBones = ['spine', 'spine.001', 'spine.002', 'spine.003', 'spine.004'].map(b);
    this.arms = [b('upper_arm.L'), b('upper_arm.R')];
    this.jaw = b('jaw');
    this.tail = ['tail.001', 'tail.002', 'tail.003', 'tail.004', 'tail.005', 'tail.006'].map(b).filter((x): x is Bone => !!x);
    let s = (seed * 9301 + 49297) % 233280;
    this.rnd = () => { s = (s * 9301 + 49297) % 233280; return s / 233280; };
    this.nextTwitch = this.rnd() * p.earTwitch;
  }

  /**
   * Puts back the clip's own rotations. The mixer only writes a bone when its clip value changes,
   * so this must run before every mixer update or the offsets would pile up.
   */
  restore(): void {
    for (const [bone, q] of this.saved) bone.quaternion.copy(q);
    this.saved.clear();
  }

  /** Call after the mixer and the build have posed the rig, and `restore()` before the mixer. */
  update(dt: number, timeS: number, i: SecondaryInput): void {
    this.rig.updateMatrixWorld(true);
    const running = i.mode === 'run' && i.speed > 0.15;
    const gallop = MathUtils.smoothstep(i.speed, 1.5, 3);

    // ---- head and neck look-at, limited and smoothed; reduced at a gallop, off while floating
    let yaw = 0, pitch = 0;
    if (i.look && this.head && (i.mode === 'settle' || i.mode === 'run' || i.mode === 'celebrate')) {
      this.head.getWorldPosition(_v);
      const d = _v.subVectors(i.look, _v).applyQuaternion(_q.copy(i.catQ).invert());
      yaw = MathUtils.clamp(Math.atan2(d.x, d.z), -0.9, 0.9);
      pitch = MathUtils.clamp(Math.atan2(d.y, Math.hypot(d.x, d.z)), -0.35, 0.45);
      if (d.z < -0.2) yaw *= 0.5; // behind: only a partial turn
    }
    const L = i.layers ?? {};
    const wander = L.lookAround ?? 0;
    if (wander > 0) {
      // looking around: slow sweeps left and right with pauses, glancing up now and then
      const sweep = Math.sin(timeS * 0.55 + this.p.energy * 7) + 0.45 * Math.sin(timeS * 1.37 + this.p.earTwitch);
      const held = Math.sign(sweep) * Math.min(1, Math.abs(sweep) * 1.4);
      yaw += (held * 0.85 - yaw) * wander;
      pitch += ((0.12 + 0.18 * Math.max(0, Math.sin(timeS * 0.31 + this.p.wobble * 20))) - pitch) * wander;
    }
    this.layers(L, timeS, i.catQ);
    const lookWeight = i.mode === 'settle' ? 1 - 0.6 * ((L.lie ?? 0) * 0.3 + (L.stretch ?? 0) + (L.face ?? 0)) : running ? 0.45 * (1 - 0.6 * gallop) : 0.6;
    const k = 1 - Math.exp(-dt * 4);
    this.yaw += (yaw * lookWeight - this.yaw) * k;
    this.pitch += (pitch * lookWeight - this.pitch) * k;
    if (this.neck) { rotate(this.neck, i.catQ, Y, this.yaw * 0.4, this.saved); rotate(this.neck, i.catQ, X, -this.pitch * 0.35, this.saved); }
    if (this.head) { rotate(this.head, i.catQ, Y, this.yaw * 0.6, this.saved); rotate(this.head, i.catQ, X, -this.pitch * 0.65, this.saved); }

    // ---- ears: occasional twitches, relaxed back while eating or floating
    this.nextTwitch -= dt;
    if (this.nextTwitch <= 0) {
      const side = this.rnd() < 0.5 ? 0 : 1;
      this.earTwitch[side] = 1;
      if (this.rnd() < 0.3) this.earTwitch[1 - side] = 0.8;
      this.nextTwitch = this.p.earTwitch * (0.4 + this.rnd() * 1.2);
    }
    const relaxTarget = i.mode === 'eat' ? 1 : i.mode === 'float' ? 0.7 : running ? 0.25 * gallop : 0;
    this.earRelax += (relaxTarget - this.earRelax) * (1 - Math.exp(-dt * 3));
    this.ears.forEach((ear, s) => {
      if (!ear) return;
      const tw = this.earTwitch[s]!;
      this.earTwitch[s] = Math.max(0, tw - dt * 5);
      const flick = Math.sin(tw * Math.PI) * 0.35;
      const out = s === 0 ? -1 : 1; // left ear sits on +X
      rotate(ear, i.catQ, Z, out * (flick + this.earRelax * 0.3), this.saved);
      rotate(ear, i.catQ, X, -(flick * 0.6 + this.earRelax * 0.35), this.saved);
    });

    // ---- tail: carried higher when running, swinging against turns and speed changes
    // carried proudly at a trot, streaming lower behind a gallop
    const liftTarget = running ? this.p.tailLift * (1 - gallop) - 0.35 * gallop : i.mode === 'settle' ? 0.15 : 0;
    this.tailLift += (liftTarget - this.tailLift) * (1 - Math.exp(-dt * 2));
    const drive = -i.yawRate * 1.6 + Math.sin(timeS * (running ? 3.1 : 1.3) + this.p.energy * 5) * (running ? 0.12 : 0.3);
    // a damped spring towards the drive
    const stiffness = 30, damping = 7;
    this.tailVel += ((drive - this.tailSway) * stiffness - this.tailVel * damping) * dt;
    this.tailSway += this.tailVel * dt;
    // tail flicks: quick lashes of the tip in bursts, now one way, now the other
    const flickW = L.flick ?? 0;
    let flick = 0;
    if (flickW > 0) {
      this.flickAt -= dt;
      if (this.flickAt <= 0) { this.flickAt = 0.9 + this.rnd() * 1.6; this.flickSide = this.rnd() < 0.5 ? -1 : 1; }
      const burst = Math.max(0, Math.sin(Math.min(1, this.flickAt / 0.9) * Math.PI));
      flick = flickW * burst;
    }
    const stretchTail = L.stretch ?? 0;
    this.tail.forEach((bone, n) => {
      const along = n / Math.max(1, this.tail.length - 1);
      rotate(bone, i.catQ, X, this.tailLift * (n === 0 ? 0.55 : 0.12) - i.accel * 0.02 * along + stretchTail * (n === 0 ? 0.7 : 0.04), this.saved);
      const lag = Math.sin(timeS * 2.2 - n * 0.7) * 0.06 * (running ? 0.4 : 1);
      const lash = flick * this.flickSide * 0.55 * along * Math.sin(timeS * 11 - n * 0.9);
      rotate(bone, i.catQ, Y, this.tailSway * 0.18 * (0.5 + along) + lag + lash, this.saved);
    });
  }

  /**
   * The podium layers. Lying down tips the chest forward over the sitting hips and reaches the
   * front legs out along the ground; a stretch reaches the front legs far forward from a stand,
   * chest low, head up and a yawn; a face wash sweeps the raised paw over the face and ear.
   */
  private layers(L: Partial<Record<PoseLayer, number>>, timeS: number, catQ: Quaternion): void {
    const [pelvis, lumbar, back, chest, neck] = this.spineBones;
    const lie = L.lie ?? 0, stretch = L.stretch ?? 0, face = L.face ?? 0;
    if (lie > 0) {
      if (pelvis) rotate(pelvis, catQ, X, LIE.pelvis * lie, this.saved);
      if (lumbar) rotate(lumbar, catQ, X, LIE.lumbar * lie, this.saved);
      if (back) rotate(back, catQ, X, LIE.back * lie, this.saved);
      if (chest) rotate(chest, catQ, X, LIE.chest * lie, this.saved);
      if (neck) rotate(neck, catQ, X, LIE.neck * lie, this.saved);
      for (const arm of this.arms) if (arm) rotate(arm, catQ, X, LIE.arm * lie, this.saved);
    }
    if (stretch > 0) {
      if (lumbar) rotate(lumbar, catQ, X, STRETCH.lumbar * stretch, this.saved);
      if (chest) rotate(chest, catQ, X, STRETCH.chest * stretch, this.saved);
      if (neck) rotate(neck, catQ, X, STRETCH.neck * stretch, this.saved);
      for (const arm of this.arms) if (arm) rotate(arm, catQ, X, STRETCH.arm * stretch, this.saved);
      // a yawn at the top of the stretch
      if (this.jaw) rotate(this.jaw, catQ, X, STRETCH.jaw * MathUtils.smoothstep(stretch, 0.6, 1), this.saved);
    }
    if (face > 0) {
      const sweep = Math.sin(timeS * 2 * Math.PI * 0.8);
      const arm = this.arms[0];
      if (arm) rotate(arm, catQ, X, (FACE.armUp + FACE.armSweep * sweep) * face, this.saved);
      if (neck) {
        rotate(neck, catQ, Z, FACE.roll * face * (0.75 + 0.25 * sweep), this.saved);
        rotate(neck, catQ, X, FACE.dip * face, this.saved);
      }
    }
  }
}

/** Lie-down offsets (radians, cat space; +X pitch tips the nose down). */
export const LIE = { pelvis: 0.08, lumbar: 0.4, back: 0.1, chest: 0.05, neck: -0.85, arm: -1.5 };
/** Stretch offsets (radians), on the standing idle. */
export const STRETCH = { lumbar: 0.18, chest: 0.22, neck: -0.55, arm: -0.95, jaw: 0.45 };
/** Face-wash offsets (radians), on the groom clip. */
export const FACE = { armUp: -0.35, armSweep: 0.25, roll: 0.35, dip: 0.15 };
